import { Button } from '../../primitives/Button.tsx';
import { Select } from '../../primitives/Select.tsx';
import { Text } from '../../primitives/Text.tsx';
import styles from './Pagination.module.css';

/**
 * Design system § 14 — Pagination.
 *
 * The range in view, the pages around the current one, previous and next, and
 * — where the caller offers one — a page-size Select.
 *
 * IT PAGES OVER A TOTAL THE SERVER COUNTED, never over an array the browser
 * already holds. `total` and `pageCount` are the server's numbers, which is
 * what makes this component honest at forty accounts and at forty thousand:
 * client-side slicing would need the whole table in memory to draw this bar
 * correctly, and would quietly stop being true the moment it did not.
 *
 * The current page carries `aria-current="page"` as well as the `ink` fill, so
 * position is never carried by colour alone (§ 12). The ends of the range
 * disable with a reason rather than disappearing — a control that vanishes
 * leaves the reader wondering where it went.
 */

interface PaginationProps {
  /** 1-based. */
  page: number;
  pageCount: number;
  /** Rows the server counted, across every page. */
  total: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  /** Offered only when the caller passes sizes to offer. */
  pageSizes?: readonly number[];
  onPageSizeChange?: (size: number) => void;
  /** What is being counted, plural and lower case: "usuários", "lições". */
  itemLabel: string;
  /** The accessible name of the navigation region. */
  label: string;
}

/**
 * The page numbers to draw: the first, the last, and a window around the
 * current one, with a gap marker where a run was left out.
 *
 * A bar that lists every page is unusable past about twenty of them, and one
 * that lists only previous/next hides how far the set actually goes.
 */
function pageWindow(page: number, pageCount: number): (number | 'gap')[] {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, index) => index + 1);

  const window = new Set<number>([1, pageCount, page]);
  if (page - 1 > 1) window.add(page - 1);
  if (page + 1 < pageCount) window.add(page + 1);
  // Keeps the bar a constant width near the ends, where the window would
  // otherwise be lopsided and the whole control would resize as you paged.
  if (page <= 3) [2, 3, 4].forEach((n) => n < pageCount && window.add(n));
  if (page >= pageCount - 2)
    [pageCount - 3, pageCount - 2, pageCount - 1].forEach((n) => n > 1 && window.add(n));

  const sorted = [...window].sort((a, b) => a - b);
  const out: (number | 'gap')[] = [];
  let previous = 0;
  for (const value of sorted) {
    if (previous && value - previous > 1) out.push('gap');
    out.push(value);
    previous = value;
  }
  return out;
}

export function Pagination({
  page,
  pageCount,
  total,
  pageSize,
  onPageChange,
  pageSizes,
  onPageSizeChange,
  itemLabel,
  label,
}: PaginationProps) {
  const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  return (
    <div className={styles.bar}>
      <Text as="p" variant="body-sm" tone="muted">
        {total === 0
          ? `Nenhum resultado`
          : `${first}–${last} de ${total} ${itemLabel}`}
      </Text>

      <div className={styles.controls}>
        {pageSizes && onPageSizeChange ? (
          <span className={styles.size}>
            <Text as="span" variant="body-sm" tone="muted">
              Por página
            </Text>
            <Select
              id="pagination-size"
              size="compact"
              ariaLabel="Resultados por página"
              value={String(pageSize)}
              options={pageSizes.map((n) => ({ value: String(n), label: String(n) }))}
              onChange={(next) => onPageSizeChange(Number(next))}
            />
          </span>
        ) : null}

        <nav className={styles.pages} aria-label={label}>
          {page <= 1 ? (
            <Button
              id="pagination-previous"
              variant="ghost"
              size="compact"
              icon="chevron-left"
              disabled
              disabledReason="Você está na primeira página."
              ariaLabel="Página anterior"
            >
              Anterior
            </Button>
          ) : (
            <Button
              id="pagination-previous"
              variant="ghost"
              size="compact"
              icon="chevron-left"
              ariaLabel="Página anterior"
              onClick={() => onPageChange(page - 1)}
            >
              Anterior
            </Button>
          )}

          <span className={styles.numbers}>
            {pageWindow(page, pageCount).map((entry, index) =>
              entry === 'gap' ? (
                <span key={`gap-${index}`} className={styles.gap} aria-hidden="true">
                  …
                </span>
              ) : entry === page ? (
                <Button
                  key={entry}
                  variant="primary"
                  size="compact"
                  ariaCurrent="page"
                  ariaLabel={`Página ${entry}, atual`}
                  onClick={() => onPageChange(entry)}
                >
                  {entry}
                </Button>
              ) : (
                <Button
                  key={entry}
                  variant="ghost"
                  size="compact"
                  ariaLabel={`Página ${entry}`}
                  onClick={() => onPageChange(entry)}
                >
                  {entry}
                </Button>
              ),
            )}
          </span>

          {page >= pageCount ? (
            <Button
              id="pagination-next"
              variant="ghost"
              size="compact"
              disabled
              disabledReason="Você está na última página."
              ariaLabel="Próxima página"
            >
              Próxima
            </Button>
          ) : (
            <Button
              id="pagination-next"
              variant="ghost"
              size="compact"
              ariaLabel="Próxima página"
              onClick={() => onPageChange(page + 1)}
            >
              Próxima
            </Button>
          )}
        </nav>
      </div>
    </div>
  );
}
